import React, { useMemo } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { IconButton, Tooltip } from '@mui/material';
import { format, formatDistanceToNow } from 'date-fns';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import { applyDefaultFilterOperators } from '@/utils/filter.ts';
import DeleteIcon from '@mui/icons-material/Delete';
import { UserFilter } from '@/API.ts';
import {
  FilterSelections,
  GraphQLQuery,
  SearchableFilterInput,
} from '@lambda-layer/types/filter.ts';
import { useServices } from '@/components/ServiceContext.tsx';
import { ServiceDataGrid } from '@/components/ServiceDataGrid';
import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import { ListType } from '@/service/ExportJobService.ts';

const UserFilterTable: React.FC<{
  listType: ListType;
  onApplyFilterSelections: (filterSelections: FilterSelections) => void;
  onDelete?: (userFilter: UserFilter) => void;
  filterDefinitions: FilterDefinition[];
}> = ({ listType, onApplyFilterSelections, onDelete }) => {
  const { userFilterService } = useServices();

  const columns: GridColDef<UserFilter>[] = useMemo(
    () =>
      applyDefaultFilterOperators([
        {
          field: '',
          headerName: 'Actions',
          filterable: false,
          width: 100,
          renderCell: params => {
            const userFilter = params.row as UserFilter;
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Tooltip title="Apply Filter">
                  <IconButton
                    onClick={() => {
                      const filterSelections = JSON.parse(
                        userFilter.filterSelections
                      ) as FilterSelections;
                      onApplyFilterSelections(filterSelections);
                    }}
                  >
                    <AutoAwesomeMotionIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => {
                      if (onDelete) {
                        onDelete(params.row as UserFilter);
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </div>
            );
          },
        },
        {
          field: 'name',
          headerName: 'Filter Name',
          width: 300,
          flex: 1,
        },
        {
          field: 'owner',
          headerName: 'User',
          width: 150,
          flex: 0.5,
        },
        {
          field: 'createdAt',
          headerName: 'Created Date',
          width: 200,
          renderCell: (params: GridRenderCellParams<UserFilter>) => {
            const date = new Date(params.value);
            const formattedDate = format(date, 'yyyy-MM-dd HH:mm:ss');
            const relativeTime = formatDistanceToNow(date, { addSuffix: true });

            return (
              <Tooltip title={formattedDate}>
                <span>{relativeTime}</span>
              </Tooltip>
            );
          },
        },
      ]),
    [onApplyFilterSelections, onDelete]
  );

  const handleQueryUpdated = React.useCallback(
    (
      query: GraphQLQuery<SearchableFilterInput<GridValidRowModel>>
    ): GraphQLQuery<SearchableFilterInput<GridValidRowModel>> => {
      query.listType = { eq: listType };
      return query;
    },
    [listType]
  );

  return (
    <>
      <div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <ServiceDataGrid
            rowSelection={false}
            service={userFilterService}
            columns={columns}
            pageSize={10}
            sortModel={[{ field: 'name', sort: 'asc' }]}
            onQueryUpdated={handleQueryUpdated}
            quickSearchFields={['name', 'owner']}
          />
        </div>
      </div>
    </>
  );
};

export default UserFilterTable;
